// client/src/components/pages/LexiStar/RouteTruckDriver/TrendChart.tsx
//
// The trend of "Routes, trucks & drivers" (ST-00254 / 255 / 256): the ranked figure per bucket of
// the period — the whole operation, or the selected row — as columns, with the comparison figure
// (allowance, plan) as a line over them so used-vs-allowed and moved-vs-planned read at a glance.
// A dashed outline marks a provisional bucket where the API said so. Theme variables for every
// colour (ST-00243); recharts children in arrays, never a fragment (ST-00260).

import { Bar, CartesianGrid, Cell, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { periodTick, type Grain } from '../Dashboard/periodLogic';
import { formatCell, formatTick, type RtdColumn, type TrendPoint } from './rtdLogic';

const tw = (name: string) => `rgb(var(--tw-${name}))`;
const PRIMARY = tw('primary-500');
const PROVISIONAL = tw('primary-300');
const COMPARE = tw('slate-700');
const GRID = tw('slate-200');
const TICK = { fontSize: 11, fill: tw('slate-600') };
const TOOLTIP_STYLE = {
  background: 'var(--surface-raised)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  color: 'var(--text)',
  fontSize: 12,
};
const TOOLTIP_LABEL = { color: 'var(--text-strong)', fontWeight: 600 };
const HEIGHT = 200;
const MARGIN = { top: 8, right: 12, bottom: 0, left: 0 };

export interface TrendChartProps {
  points: TrendPoint[];
  column: RtdColumn;
  compare: RtdColumn | null;
  grain: Grain;
  /** Whose trend this is (the figcaption). */
  subject: string;
}

export default function TrendChart({ points, column, compare, grain, subject }: TrendChartProps) {
  const data = points.map((p) => ({
    label: p.label,
    tick: periodTick(p.label, grain),
    state: p.state,
    primary: p.values[column.key] ?? null,
    compare: compare ? p.values[compare.key] ?? null : null,
  }));
  const withFigures = data.filter((d) => d.primary !== null);
  const summary = withFigures.length
    ? `${column.label} for ${subject} over ${data.length} ${grain === 'day' ? 'days' : grain === 'week' ? 'weeks' : 'months'}: from ${formatCell(
        column,
        withFigures[0]!.primary
      )} to ${formatCell(column, withFigures[withFigures.length - 1]!.primary)}${
        compare ? `, with ${compare.label.toLowerCase()} as the line` : ''
      }.`
    : `No ${column.label.toLowerCase()} for ${subject} in this period.`;
  return (
    <figure className="m-0" data-testid="trend-chart" data-points={data.length} aria-label={summary}>
      <div style={{ width: '100%', height: HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={MARGIN} barCategoryGap="30%">
            {[
              <CartesianGrid key="g" vertical={false} stroke={GRID} strokeDasharray="3 3" />,
              <XAxis key="x" dataKey="tick" tick={TICK} tickLine={false} axisLine={{ stroke: GRID }} interval="preserveStartEnd" minTickGap={16} />,
              <YAxis key="y" tick={TICK} tickLine={false} axisLine={false} width={48} tickFormatter={(v: number) => formatTick(column, v)} />,
              <Tooltip
                key="t"
                isAnimationActive={false}
                cursor={{ fill: 'rgb(var(--tw-slate-100))' }}
                contentStyle={TOOLTIP_STYLE}
                labelStyle={TOOLTIP_LABEL}
                formatter={(v: unknown, name: string) => [
                  formatCell(name === column.label ? column : compare || column, typeof v === 'number' ? v : null),
                  name,
                ]}
              />,
              ...(compare ? [<Legend key="l" iconType="plainline" wrapperStyle={{ fontSize: 11, color: 'var(--text)' }} />] : []),
              <Bar key="b" dataKey="primary" name={column.label} fill={PRIMARY} maxBarSize={28} radius={[3, 3, 0, 0]} isAnimationActive={false}>
                {data.map((d) => (
                  <Cell
                    key={d.label}
                    fill={d.state === 'provisional' ? PROVISIONAL : PRIMARY}
                    stroke={d.state === 'provisional' ? PRIMARY : undefined}
                    strokeDasharray={d.state === 'provisional' ? '3 2' : undefined}
                  />
                ))}
              </Bar>,
              ...(compare
                ? [
                    <Line
                      key="c"
                      type="linear"
                      dataKey="compare"
                      name={compare.label}
                      stroke={COMPARE}
                      strokeWidth={2}
                      dot={data.length <= 31 ? { r: 2.5, strokeWidth: 0, fill: COMPARE } : false}
                      isAnimationActive={false}
                      connectNulls
                    />,
                  ]
                : []),
            ]}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <figcaption className="sr-only">{summary}</figcaption>
    </figure>
  );
}
